import api from './api';
import type {
  RenewalBatch,
  RenewalBatchFilters,
  ServicingRequest,
  ServicingRequestFilters,
  PaginatedResponse,
} from '@/types';
import type {
  RenewalQueueActivityPayload,
  RenewalQueueActivityResponse,
  RenewalQueueDetail,
  RenewalQueueFilters,
  RenewalQueueRow,
  RenewalQueueUpdateStatusPayload,
  RenewalQueueWorkflowActionResponse,
  RenewalPaymentConfirmPayload,
  RenewalPaymentLink,
  RenewalPaymentLinkActionResponse,
  RenewalPaymentLinkCreatePayload,
} from '@/types/renewalQueue';

/**
 * B2: the detail payload is much richer than the list row -- description,
 * requester contact, SLA, resolution and the prefetched `documents` /
 * `activities` collections that `ServiceRequestDetailSerializer` returns. The
 * shared `ServicingRequest` type in `@/types` describes the LIST shape (and its
 * `status` / `request_type` unions do not match the model's choices), so the
 * detail shape is declared here rather than widening a type several other
 * screens depend on.
 */
export interface ServicingRequestDocument {
  id: string;
  name: string;
  document_type: string;
  file: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface ServicingRequestActivity {
  id: string;
  activity_type: string;
  description: string;
  performed_by: string | null;
  performed_by_name: string | null;
  activity_date: string;
  old_status: string;
  new_status: string;
  activity_data: Record<string, unknown>;
  created_at: string;
}

export interface ServicingRequestDetail {
  id: string;
  request_number: string;
  policy: string;
  policy_number: string;
  insurer_policy_number: string | null;
  /** U-05: the policy's insurer -- id, name, logo and blurb. Logo/blurb are
   *  '' when the insurer has none. */
  insurer?: string | null;
  insurer_name?: string;
  insurer_logo_url?: string;
  insurer_description?: string;
  customer_name: string | null;
  request_type: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  requested_by: string | null;
  requester_name: string;
  requester_email: string;
  requester_phone: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_at: string | null;
  sla_due_date: string | null;
  is_sla_breached: boolean;
  resolution: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  rejection_reason: string;
  endorsement: string | null;
  request_data: Record<string, unknown>;
  notes: string;
  documents: ServicingRequestDocument[];
  activities: ServicingRequestActivity[];
  created_at: string;
  updated_at: string;
}

export interface ServicingRequestStatusUpdate {
  status: string;
  resolution?: string;
  rejection_reason?: string;
}

/**
 * B9: `bucket`/`days` are the queue's real window (§2.2); `lapsed_days` is an
 * optional lower bound for `lapsed`. B1 adds `month`/`year`, a further pin
 * onto one calendar month/year of `queue_date`.
 */
function appendRenewalQueueFilters(params: URLSearchParams, filters?: RenewalQueueFilters): void {
  if (!filters) return;
  if (filters.bucket) params.append('bucket', filters.bucket);
  if (filters.days !== undefined) params.append('days', String(filters.days));
  if (filters.lapsed_days !== undefined) params.append('lapsed_days', String(filters.lapsed_days));
  if (filters.month !== undefined) params.append('month', String(filters.month));
  if (filters.year !== undefined) params.append('year', String(filters.year));
  if (filters.status) params.append('status', filters.status);
  if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters.search) params.append('search', filters.search);
  if (filters.insurer) params.append('insurer', filters.insurer);
  if (filters.product) params.append('product', filters.product);
  if (filters.insurance_type) params.append('insurance_type', filters.insurance_type);
  if (filters.posp) params.append('posp', filters.posp);
  if (filters.customer) params.append('customer', filters.customer);
  if (filters.customer_group) params.append('customer_group', filters.customer_group);
  if (filters.archived) params.append('archived', filters.archived);
  if (filters.renewal_payment) params.append('renewal_payment', filters.renewal_payment);
  if (filters.ordering) params.append('ordering', filters.ordering);
}

export const renewalService = {
  // `getRenewals`/`getRenewal` (`GET /renewals/` and `/renewals/{id}/` by the
  // legacy id-keyed route) and their writers -- `updateRenewal` (PATCH, now
  // 405 since B9 made `RenewalViewSet` read-only), `assignRenewal` and
  // `updateRenewalStatus` -- had no caller left once the policy-keyed queue
  // (`getRenewalQueue` / `assignRenewalQueueItem` / `updateRenewalQueueItemStatus`
  // below) took over. Removed rather than kept dead; `GET /renewals/` itself
  // is still reachable directly for the notification-link history lookup
  // where a screen needs one (none does today).
  //
  // B9 removed `GET /renewals/summary/` (queue #242's lapsed/upcoming badge
  // counts). Its policy-queue successor `getRenewalQueueSummary` fed the
  // Upcoming/Lapsed tab counts until 22 Sep (Cust-4/U-03), when the owner
  // asked for the counts to go; with no caller left it was deleted too.
  // `GET /renewals/queue/summary/` itself still exists server-side.

  // B9: the policy-based renewal queue (docs/superpowers/specs/2026-09-15-b9-plan.md
  // §2). Queue membership is decided by policy data, not by a materialised
  // Renewal row -- there is no "fetch pending" here because there is nothing
  // left to materialise; a row simply appears when its policy qualifies.
  async getRenewalQueue(
    page: number = 1,
    filters?: RenewalQueueFilters
  ): Promise<PaginatedResponse<RenewalQueueRow>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    appendRenewalQueueFilters(params, filters);
    const response = await api.get<PaginatedResponse<RenewalQueueRow>>(
      `/renewals/queue/?${params.toString()}`
    );
    return response.data;
  },

  /** `id` may be a policy id or a legacy Renewal id -- the backend resolves
   *  either within scope. */
  async getRenewalQueueItem(id: string): Promise<RenewalQueueDetail> {
    const response = await api.get<RenewalQueueDetail>(`/renewals/queue/${id}/`);
    return response.data;
  },

  /** `{message, renewal, item}` -- `item` is the re-fetched queue detail row. */
  async assignRenewalQueueItem(
    policyId: string,
    assignedTo: string
  ): Promise<RenewalQueueWorkflowActionResponse> {
    const response = await api.post<RenewalQueueWorkflowActionResponse>(
      `/renewals/queue/${policyId}/assign/`,
      {
        assigned_to: assignedTo,
      }
    );
    return response.data;
  },

  /** `{message, renewal, item}` -- `item` is the re-fetched queue detail row. */
  async updateRenewalQueueItemStatus(
    policyId: string,
    payload: RenewalQueueUpdateStatusPayload
  ): Promise<RenewalQueueWorkflowActionResponse> {
    const response = await api.post<RenewalQueueWorkflowActionResponse>(
      `/renewals/queue/${policyId}/update_status/`,
      payload
    );
    return response.data;
  },

  async logRenewalQueueItemActivity(
    policyId: string,
    payload: RenewalQueueActivityPayload
  ): Promise<RenewalQueueActivityResponse> {
    const response = await api.post<RenewalQueueActivityResponse>(
      `/renewals/queue/${policyId}/log_activity/`,
      payload
    );
    return response.data;
  },

  // #345: the insurer's renewal payment link (desk side). The URL comes back
  // only from `createPaymentLink` and the audited `revealPaymentLink`.
  async listPaymentLinks(policyId: string): Promise<RenewalPaymentLink[]> {
    const response = await api.get<RenewalPaymentLink[]>(`/renewals/queue/${policyId}/payment-links/`);
    return response.data;
  },

  async createPaymentLink(
    policyId: string,
    payload: RenewalPaymentLinkCreatePayload
  ): Promise<RenewalPaymentLinkActionResponse & { url: string }> {
    const response = await api.post<RenewalPaymentLinkActionResponse & { url: string }>(
      `/renewals/queue/${policyId}/payment-links/`,
      payload
    );
    return response.data;
  },

  async revokePaymentLink(linkId: string, reason = ''): Promise<RenewalPaymentLinkActionResponse> {
    const response = await api.post<RenewalPaymentLinkActionResponse>(
      `/renewals/payment-links/${linkId}/revoke/`,
      { reason }
    );
    return response.data;
  },

  async rejectPaymentReport(linkId: string, reason = ''): Promise<RenewalPaymentLinkActionResponse> {
    const response = await api.post<RenewalPaymentLinkActionResponse>(
      `/renewals/payment-links/${linkId}/reject-report/`,
      { reason }
    );
    return response.data;
  },

  async revealPaymentLink(linkId: string): Promise<{ id: string; url: string }> {
    const response = await api.post<{ id: string; url: string }>(`/renewals/payment-links/${linkId}/reveal/`);
    return response.data;
  },

  /** Multipart: the insurer's receipt file is optional (without one the server
   *  generates a clearly labelled acknowledgement). */
  async confirmRenewalPayment(
    linkId: string,
    payload: RenewalPaymentConfirmPayload
  ): Promise<RenewalPaymentLinkActionResponse> {
    const form = new FormData();
    form.append('insurer_reference', payload.insurer_reference);
    form.append('paid_at', payload.paid_at);
    if (payload.amount) form.append('amount', payload.amount);
    if (payload.additional_payment) form.append('additional_payment', 'true');
    if (payload.receipt_file) form.append('receipt_file', payload.receipt_file);
    const response = await api.post<RenewalPaymentLinkActionResponse>(
      `/renewals/payment-links/${linkId}/confirm-payment/`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // #345: the customer's own two taps (web portal). `pay` answers with the
  // insurer's URL only for the policy's own customer.
  async startRenewalPayment(
    policyId: string
  ): Promise<{ redirect_url: string; host: string; amount: string; currency: string; expires_at: string | null }> {
    const response = await api.post(`/renewals/queue/${policyId}/pay/`);
    return response.data;
  },

  async reportRenewalPaid(policyId: string, receipt?: File | null): Promise<void> {
    const form = new FormData();
    if (receipt) form.append('receipt_file', receipt);
    await api.post(`/renewals/queue/${policyId}/report-paid/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Batches
  async getBatches(
    page: number = 1,
    filters?: RenewalBatchFilters
  ): Promise<PaginatedResponse<RenewalBatch>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);

    const response = await api.get<PaginatedResponse<RenewalBatch>>(
      `/renewals/batches/?${params.toString()}`
    );
    return response.data;
  },

  async getBatch(id: string): Promise<RenewalBatch> {
    const response = await api.get<RenewalBatch>(`/renewals/batches/${id}/`);
    return response.data;
  },

  async createBatch(data: Partial<RenewalBatch>): Promise<RenewalBatch> {
    const response = await api.post<RenewalBatch>('/renewals/batches/', data);
    return response.data;
  },

  async updateBatch(id: string, data: Partial<RenewalBatch>): Promise<RenewalBatch> {
    const response = await api.patch<RenewalBatch>(`/renewals/batches/${id}/`, data);
    return response.data;
  },

  // Servicing Requests
  async getServicingRequests(
    page: number = 1,
    filters?: ServicingRequestFilters
  ): Promise<PaginatedResponse<ServicingRequest>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.request_type) params.append('request_type', filters.request_type);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters?.ordering) params.append('ordering', filters.ordering);

    const response = await api.get<PaginatedResponse<ServicingRequest>>(
      `/renewals/service-requests/?${params.toString()}`
    );
    return response.data;
  },

  async getServicingRequest(id: string): Promise<ServicingRequestDetail> {
    const response = await api.get<ServicingRequestDetail>(`/renewals/service-requests/${id}/`);
    return response.data;
  },

  /** B2: drive a request forward. Broker side always; a POSP only when the
   *  Broker Admin granted `can_resolve_service_requests`; never a customer --
   *  all three gates live in `ServiceRequestViewSet.get_permissions`. */
  async updateServicingRequestStatus(
    id: string,
    payload: ServicingRequestStatusUpdate
  ): Promise<ServicingRequestDetail> {
    const response = await api.post<{ message: string; service_request: ServicingRequestDetail }>(
      `/renewals/service-requests/${id}/update_status/`,
      payload
    );
    return response.data.service_request;
  },

  /** #375: Broker Admin only. Returns a closed request to In Progress; the
   *  reason is required and lands in the request's audit trail. */
  async reopenServicingRequest(id: string, reason: string): Promise<ServicingRequestDetail> {
    const response = await api.post<{ message: string; service_request: ServicingRequestDetail }>(
      `/renewals/service-requests/${id}/reopen/`,
      { reason }
    );
    return response.data.service_request;
  },

  async createServicingRequest(data: Partial<ServicingRequest>): Promise<ServicingRequest> {
    const response = await api.post<ServicingRequest>('/renewals/service-requests/', data);
    return response.data;
  },
};
